import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { BagReportDialog, Button, WarningXl } from "@ptsd/uikit";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { Dialog, Form, useMedia, View, XStack } from "tamagui";
import { z } from "zod";

import { api } from "~/utils/api";

const FILE_MINE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/bmp",
  "image/tiff",
  "image/heif",
  "image/heic",

  "video/avi",
  "video/mp4",
  "video/mpeg",
  "video/quicktime",
  "video/x-msvideo",
  "video/x-ms-wmv",
  "video/x-flv",
  "video/webm",
  "video/3gpp",
  "video/3gpp2",
  "video/ogg",
];
const FILE_SIZE_LIMIT = 10 * 1024 * 1024;

const feedBackFormSchema = z.object({
  message: z
    .string()
    .min(10, { message: "Текст повинен містити принаймні 10 символів." })
    .max(270, { message: "Текст не може містити більше ніж 1000 символів." }),
  file: z
    .instanceof(File)
    .refine((file) => FILE_MINE_TYPES.includes(file.type), {
      message: "Дозволені лише фото та відео",
    })
    .refine((file) => file.size <= FILE_SIZE_LIMIT, {
      message: "Файл повинен бути не більше 10MB",
    })
    .optional(),
});

type FeedBackFormData = z.infer<typeof feedBackFormSchema>;

export const BagReportForm: React.FC<{
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  closeDialog: () => void;
}> = ({ open, setOpen, closeDialog }) => {
  const { sm } = useMedia();
  const { control, handleSubmit } = useForm<FeedBackFormData>({
    defaultValues: {
      message: "",
      file: undefined,
    },
    resolver: zodResolver(feedBackFormSchema),
  });

  const { mutateAsync: getPresignedUrl } =
    api.messages.getPresignedUrlToUploadAssets.useMutation();

  const { mutate: sendBagReportToSlack } =
    api.messages.sendBagReportToSlack.useMutation({
      onSuccess: () => closeDialog(),
    });

  const onSubmit: SubmitHandler<FeedBackFormData> = async ({
    message,
    file,
  }) => {
    if (file) {
      const { url, fields } = await getPresignedUrl({
        fileType: file.type,
        fileName: file.name,
      });

      const data = {
        ...fields,
        "Content-Type": file.type,
        file,
      };
      const formData = new FormData();
      for (const name in data) {
        formData.append(name, data[name as keyof typeof data]);
      }
      await fetch(url, {
        method: "POST",
        body: formData,
      });

      sendBagReportToSlack({
        message,
        assetName: fields.key?.split("/")[1],
      });
    }
    if (!file) {
      sendBagReportToSlack({
        message,
      });
    }
  };

  return (
    <Dialog data-id={"bag-report-dialog"} onOpenChange={setOpen} open={open}>
      <Dialog.Portal>
        <BagReportDialog.Overlay
          key={"bag-report-dialog-overlay"}
          data-id={"bag-report-dialog-overlay"}
        />
        <BagReportDialog.Content
          data-id={"bag-report-dialog-content"}
          media={sm ? "md" : "lg"}
          backgroundColor={"white"}
          gap={"$3"}
          $sm={{
            marginHorizontal: "$0.9",
          }}
        >
          <View alignItems="center" gap={"$1"}>
            <WarningXl />

            <View gap={"$0.2"}>
              <BagReportDialog.Title
                font={sm ? "md" : "lg"}
                textAlign="center"
                data-id={"bag-report-dialog-title"}
              >
                Щось пішло не так?
              </BagReportDialog.Title>
              <BagReportDialog.Description
                textAlign="center"
                data-id={"bag-report-dialog-description"}
              >
                Опишіть проблему з якою ви стикнулись та ми її виправимо
                найближчим часом
              </BagReportDialog.Description>
            </View>
          </View>

          <Form
            data-id={"bag-report-form"}
            gap={"$6"}
            $sm={{
              gap: "$3",
            }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <View
              gap={"$4"}
              $sm={{
                gap: "$3",
              }}
            >
              <Controller
                control={control}
                name="message"
                render={({ field, fieldState }) => (
                  <BagReportDialog.TextAreaWithLabel
                    label="Опишіть що саме пішло не так?"
                    placeholder="Напишіть пару слів, та додайте скріншоти нижче"
                    error={fieldState.error?.message}
                    onChangeText={(text) => field.onChange(text)}
                    {...field}
                  />
                )}
              />
              {/*sm ? (
                <Controller
                  control={control}
                  name="file"
                  render={({ field, fieldState }) => (
                    <View gap={"$1"}>
                      <Text
                        data-id={"bag-report-dialog-file-label"}
                        fontFamily={"$body"}
                        fontWeight={"$5"}
                        fontSize={"$3"}
                        lineHeight={"$3"}
                        textAlign={"center"}
                      >
                        Докладіть файл або відео (опціонально):
                      </Text>
                      <FilePicker
                        data-id={"bag-report-dialog-file-picker"}
                        onChange={(e) => {
                          if (e.target?.files?.[0]) {
                            setFileName(e.target.files[0].name);
                            field.onChange(e.target.files[0]);
                          }
                        }}
                        variant={"gray"}
                        rounded={"lg"}
                        size={"md"}
                      >
                        Додати файл
                      </FilePicker>*
                      {fieldState.error?.message && (
                        <Text
                          data-id={"bag-report-dialog-file-error"}
                          ellipsizeMode={"tail"}
                          numberOfLines={1}
                          position={"absolute"}
                          left={"$2"}
                          backgroundColor={"white"}
                          paddingHorizontal={2}
                          color={"$error-500"}
                          fontFamily={"$subtitle"}
                          fontSize={"$1"}
                          lineHeight={"$1"}
                          fontWeight={"$4"}
                        >
                          {fieldState.error?.message}
                        </Text>
                      )}
                    </View>
                  )}
                />
              ) : (
                <Controller
                  control={control}
                  name="file"
                  render={({ field, fieldState }) => (
                    <BagReportDialog.FileInput
                      data-id={"bag-report-dialog-file-input"}
                      justifyContent="center"
                      mineTypes={FILE_MINE_TYPES}
                      alignItems="center"
                      label="Докладіть файл або відео (опціонально):"
                      error={fieldState.error?.message}
                      onChange={(e) => {
                        if (e.target?.files?.[0]) {
                          setFileName(e.target.files[0].name);
                          field.onChange(e.target.files[0]);
                        }
                      }}
                    >
                      {fileName ? (
                        <Text
                          data-id={"bag-report-dialog-file-name"}
                          fontFamily={"$body"}
                          fontWeight={"$4"}
                          fontSize={"$3"}
                          lineHeight={"$3"}
                          color={"$gray-800"}
                        >
                          {fileName}
                        </Text>
                      ) : (
                        <Text
                          data-id={"bag-report-dialog-file-placeholder"}
                          fontFamily={"$body"}
                          fontWeight={"$4"}
                          fontSize={"$3"}
                          lineHeight={"$3"}
                          color={"$gray-800"}
                        >
                          Перетягніть файл у цю зону, або{" "}
                          <Text
                            fontFamily={"$body"}
                            fontWeight={"$4"}
                            fontSize={"$3"}
                            lineHeight={"$3"}
                            color={"$lightblue-700"}
                            textDecorationLine="underline"
                          >
                            натисніть тут
                          </Text>
                        </Text>
                      )}
                    </BagReportDialog.FileInput>
                  )}
                />
              )*/}
            </View>
            <XStack width={"100%"} gap={"$2"}>
              <Dialog.Close asChild>
                <Button
                  data-id={"bag-report-dialog-close-button"}
                  flex={1}
                  variant="outlined"
                  size="md"
                  rounded="lg"
                  {...(sm && {
                    size: "sm",
                  })}
                >
                  Скасувати
                </Button>
              </Dialog.Close>
              <Form.Trigger asChild>
                <Button
                  data-id={"bag-report-dialog-submit-button"}
                  flex={1}
                  variant="primary"
                  size="md"
                  rounded="lg"
                  {...(sm && {
                    size: "sm",
                  })}
                >
                  Підтвердити
                </Button>
              </Form.Trigger>
            </XStack>
          </Form>
        </BagReportDialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};
